import { Button as SButton, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import type { VariantProps } from "class-variance-authority";
import * as React from "react";

type SButtonVariantProps = VariantProps<typeof buttonVariants>;
type CustomVariants = "muted" | "active";

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color">,
    Omit<SButtonVariantProps, "variant"> {
  asChild?: boolean;
  variant?: SButtonVariantProps["variant"] | CustomVariants;
  animateOnTap?: boolean;
}

const customVariantClasses: Record<CustomVariants, string> = {
  muted:
    "h-11 rounded-full border border-[#d5d5d5] bg-white text-[rgba(34,34,34,0.5)] text-lg font-normal hover:bg-gray-50 cursor-pointer",
  active:
    "h-11 rounded-full bg-black text-white text-lg font-semibold hover:bg-gray-900 cursor-pointer",
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, animateOnTap = true, disabled, children, ...props },
    ref,
  ) => {
    const buttonContent = (
      <SButton
        {...props}
        ref={ref}
        disabled={disabled}
        variant={
          variant === "muted" || variant === "active" ? undefined : variant
        }
        className={cn(
          variant === "muted" || variant === "active"
            ? customVariantClasses[variant]
            : undefined,
          "transition-colors duration-200",
          "disabled:pointer-events-auto disabled:cursor-not-allowed",
          className,
        )}
      >
        {children}
      </SButton>
    );

    if (!animateOnTap || disabled) {
      return buttonContent;
    }

    return (
      <motion.div
        whileTap={{ scale: 0.95 }}
        transition={{
          type: "spring",
          stiffness: 400,
          damping: 17,
        }}
      >
        {buttonContent}
      </motion.div>
    );
  },
);

Button.displayName = "Button";

// Export AnimatedButton as an alias for backward compatibility
export const AnimatedButton = Button;

export { Button, buttonVariants };
